import { Button } from '@/components/Button';
import { TypedInput } from '@/components/Form/Input/TypedInput';
import { RadioButton } from '@/components/Form/RadioButton/RadioButton';
import { Select } from '@/components/Form/Select/Select';
import { TextArea } from '@/components/Form/TextArea/TextArea';
import { Modal } from '@/components/Modal';
import { Paragraph } from '@/components/Paragraph';
import { NATIONALITY_OPTIONS } from '@/constants/form';
import { useItineraryTraveler } from '@/handlers/useItineraryTraveler/useItineraryTraveler';
import { StyledFlexColumn, StyledFlexRow } from '@/layout/FlexLayout';

import type { ItineraryTravelerType } from '@/types/travelers';

interface MainBookerModalProps {
  onClose: () => void;
  travelerType: ItineraryTravelerType;
}

export function ItineraryTravelerModal({ onClose, travelerType }: MainBookerModalProps) {
  const { fields, formData, handleValue, handleSave, isSaving, isValid } = useItineraryTraveler({
    onSaved: onClose,
    travelerType,
  });
  const isMainBooker = travelerType === 'Main Booker';
  const formTitle = isMainBooker ? 'Main booker details' : 'Fellow traveler data';

  return (
    <Modal onClose={onClose} fullwidth>
      <StyledFlexColumn gap={5} padding={10}>
        <h3>{formTitle}</h3>
        <TypedInput
          label={fields.callName.label}
          required={fields.callName.required}
          value={formData.callName}
          fieldKey="callName"
          handleValue={(key, value) => handleValue(key, value)}
        />
        <StyledFlexRow gap={3}>
          <TypedInput
            label={fields.firstName.label}
            required={fields.firstName.required}
            value={formData.firstName}
            handleValue={handleValue}
            fieldKey="firstName"
          />
          <TypedInput
            label={fields.lastName.label}
            required={fields.lastName.required}
            value={formData.lastName}
            handleValue={handleValue}
            fieldKey="lastName"
          />
        </StyledFlexRow>
        <StyledFlexColumn gap={2}>
          <Paragraph weight={600}>Geslacht</Paragraph>
          <RadioButton handleValue={handleValue} fieldKey="sex" label="Man" value="M" checked={formData.sex === 'M'} />
          <RadioButton
            handleValue={handleValue}
            fieldKey="sex"
            label="Woman"
            value="F"
            checked={formData.sex === 'F'}
          />
        </StyledFlexColumn>
        {isMainBooker && (
          <StyledFlexRow gap={3}>
            <TypedInput
              label={fields.email.label}
              required={fields.email.required}
              value={'email' in formData ? formData.email : ''}
              handleValue={handleValue}
              fieldKey="email"
            />
            <TypedInput
              label={fields.phoneNumber.label}
              required={fields.phoneNumber.required}
              value={'phoneNumber' in formData ? formData?.phoneNumber : ''}
              handleValue={handleValue}
              fieldKey="phoneNumber"
            />
          </StyledFlexRow>
        )}
        <TypedInput
          label={fields.birthDate.label}
          required={fields.birthDate.required}
          value={formData.birthDate}
          handleValue={handleValue}
          type={fields.birthDate.type}
          fieldKey="birthDate"
        />
        <TypedInput
          label={fields.birthPlace.label}
          required={fields.birthPlace.required}
          value={formData.birthPlace}
          handleValue={handleValue}
          fieldKey="birthPlace"
        />
        <StyledFlexColumn gap={1}>
          <Select
            label={fields.nationality.label}
            required={fields.nationality.required}
            fieldKey="nationality"
            options={NATIONALITY_OPTIONS}
            value={formData.nationality}
            handleValue={handleValue}
          />
          <Paragraph>
            Please note! Outside Europe, an ID card is not a valid travel document. For our Belgian travellers: you need
            outside Europe an international travel pass.
          </Paragraph>
        </StyledFlexColumn>
        <StyledFlexColumn gap={1}>
          <TypedInput
            label={fields.passportNumber.label}
            required={fields.passportNumber.required}
            value={formData.passportNumber}
            handleValue={handleValue}
            fieldKey="passportNumber"
          />
          <Paragraph>
            Do you need to apply for a new travel document first? Then fill in ‘XXX’ and give us these details as soon
            as you have received it. You can then add fictitious dates below.
          </Paragraph>
        </StyledFlexColumn>
        <StyledFlexRow gap={3}>
          <TypedInput
            type={fields.passportIssueDate.type}
            label={fields.passportIssueDate.label}
            required={fields.passportIssueDate.required}
            value={formData.passportIssueDate}
            handleValue={handleValue}
            fieldKey="passportIssueDate"
          />
          <TypedInput
            type={fields.passportExpiryDate.type}
            label={fields.passportExpiryDate.label}
            required={fields.passportExpiryDate.required}
            value={formData.passportExpiryDate}
            handleValue={handleValue}
            fieldKey="passportExpiryDate"
          />
        </StyledFlexRow>

        {isMainBooker && (
          <StyledFlexColumn gap={3}>
            <Paragraph weight={600}>Address</Paragraph>
            <StyledFlexRow gap={3}>
              <TypedInput
                required={fields.zipCode.required}
                placeholder={fields.zipCode.label}
                value={'zipCode' in formData ? formData?.zipCode : ''}
                handleValue={handleValue}
                fieldKey="zipCode"
              />
              <TypedInput
                required={fields.houseNumber.required}
                value={'houseNumber' in formData ? formData?.houseNumber : ''}
                placeholder={fields.houseNumber.label}
                handleValue={handleValue}
                fieldKey="houseNumber"
              />
            </StyledFlexRow>
            <StyledFlexRow gap={3}>
              <TypedInput
                required={fields.street.required}
                value={'street' in formData ? formData?.street : ''}
                handleValue={handleValue}
                fieldKey="street"
                placeholder={fields.street.label}
              />
              <TypedInput
                required={fields.city.required}
                value={'city' in formData ? formData?.city : ''}
                handleValue={handleValue}
                fieldKey="city"
                placeholder={fields.city.label}
              />
            </StyledFlexRow>
          </StyledFlexColumn>
        )}

        <TypedInput
          label={fields.mealPreference.label}
          placeholder="Heb je dieetwensen waar we rekening mee moeten houden?"
          required={fields.mealPreference.required}
          value={formData.mealPreference}
          handleValue={handleValue}
          fieldKey="mealPreference"
        />
        {isMainBooker && (
          <>
            <StyledFlexColumn gap={1}>
              <TypedInput
                handleValue={handleValue}
                fieldKey="mainDriver"
                label={fields.mainDriver.label}
                placeholder="Wie wordt de hoofdbestuurder indien je een huurauto hebt"
                value={'mainDriver' in formData ? formData?.mainDriver : ''}
              />
              <Paragraph>Let op: de hoofdbestuurder dient in het bezit te zijn van een creditcard!</Paragraph>
            </StyledFlexColumn>
            <TextArea
              label={fields.remarks.label}
              placeholder="Heb je nog opmerkingen of vragen?"
              value={'remarks' in formData ? formData?.remarks : ''}
              handleValue={handleValue}
              fieldKey="remarks"
            />
            <h3>Gegevens thuisblijver</h3>
            <TypedInput
              label={fields.emergencyContactName.label}
              required
              value={'emergencyContactName' in formData ? formData?.emergencyContactName : ''}
              handleValue={handleValue}
              fieldKey="emergencyContactName"
            />
            <TypedInput
              label={fields.emergencyContactPhoneNumber.label}
              required
              value={'emergencyContactPhoneNumber' in formData ? formData?.emergencyContactPhoneNumber : ''}
              handleValue={handleValue}
              fieldKey="emergencyContactPhoneNumber"
            />
          </>
        )}
        <Paragraph>Let op! Na het verzenden van de gegevens kan je ze niet meer aanpassen</Paragraph>
        <StyledFlexRow gap={3} ml="auto">
          <Button onClick={onClose} variant="secondary">
            Annuleren
          </Button>
          <Button onClick={() => handleSave()} variant="primary" disabled={isSaving || !isValid}>
            Gegevens opslaan
          </Button>
        </StyledFlexRow>
      </StyledFlexColumn>
    </Modal>
  );
}
